/**
 * React Query mutation hook for disconnecting a Google account.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { crmQueryKeys } from '@/lib/crm/query/queries';
import { ApiError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';

export interface DisconnectParams {
  accountId: number;
  orgId?: number;
}

interface DisconnectResponse {
  success?: boolean;
  message?: string;
}

async function disconnectGoogle(
  params: DisconnectParams
): Promise<DisconnectResponse> {
  const response = await fetchWithDefaults(
    '/users/api/social-connections/disconnect/',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account_id: params.accountId,
        org_id: params.orgId,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Throw ApiError so callers can branch on status (4xx → surface backend
    // copy verbatim; 5xx → fall back to generic friendly copy).
    throw new ApiError(
      errorData.error || `Disconnect failed: ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: (_data, variables) => {
      // Invalidate all Google queries to refresh connection status
      queryClient.invalidateQueries({ queryKey: ['google'] });
      if (variables.orgId) {
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(
            variables.orgId,
            'ahoy_external_integration'
          ),
        });
      }
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    variables: mutation.variables,
  };
}
